import { Briefcase, Headphones, DollarSign, Users, Globe, ShoppingCart } from 'lucide-react';

export const servicePillars = [
  {
    id: 'business-administrative-support',
    slug: 'business-administrative-support',
    title: 'Business & Administrative Support',
    shortTitle: 'Business Admin',
    tagline: 'The operational backbone behind every successful business',
    icon: Briefcase,
    color: '#0E5A57',
    description: 'From virtual assistance to back-office operations, our Business & Administrative Support pillar manages the essential day-to-day functions that keep your organisation running efficiently — freeing your leadership team to focus on growth.',
    services: [
      'Administrative Support',
      'Virtual Assistance',
      'Data Entry & Processing',
      'Document Management',
      'Back Office Operations',
      'CRM Administration',
      'Scheduling & Coordination',
    ],
    benefits: [
      'Reclaim 10–15 hours per week for leadership focus',
      'Consistent, professional administrative standards across all functions',
      'Scalable capacity without the cost of full-time hires',
      'Documented workflows ensuring operational continuity',
    ],
    useCases: [
      'A growing SME overwhelmed by administrative tasks that require an extra pair of capable hands',
      'An executive needing dedicated VA support across time zones',
      'A business with high data processing volumes requiring accuracy and speed',
      'An organisation migrating CRM systems and needing intensive data management support',
    ],
    seoTitle: 'Business & Administrative Support Services | NKP VENTURES',
    seoDesc: 'Professional business and administrative support — virtual assistance, data entry, document management, back-office operations, and CRM administration.',
    faqs: [
      { q: 'How quickly can administrative support be deployed?', a: 'After a discovery call and onboarding session, we typically begin within 3–5 business days.' },
      { q: 'Do you work across time zones?', a: 'Yes. We support clients across the UK, US, Canada, Europe, Australia, and New Zealand.' },
      { q: 'What tools does your team use?', a: 'We adapt to your existing tools — email, Slack, Teams, or any CRM or project management platform you already use.' },
    ],
  },
  {
    id: 'customer-experience',
    slug: 'customer-experience',
    title: 'Customer Experience',
    shortTitle: 'Customer Experience',
    tagline: 'Every interaction handled with professionalism and care',
    icon: Headphones,
    color: '#2E7D7A',
    description: 'Our Customer Experience pillar provides professional, brand-aligned support across email, chat, and ticketing platforms — ensuring consistent service quality at every customer touchpoint, at any scale.',
    services: [
      'Customer Support',
      'Email Support',
      'Chat Support',
      'Customer Service',
      'Order Support',
      'Ticket Management',
      'Customer Follow-up',
      'Helpdesk Management',
    ],
    benefits: [
      'Consistent, professional response quality across all channels',
      'Faster response times with structured support workflows',
      'Scalable capacity for peak trading and seasonal periods',
      'Reduced cost per support interaction versus in-house teams',
    ],
    useCases: [
      'An e-commerce brand scaling rapidly and needing reliable customer support coverage',
      'A SaaS business requiring professional first-line helpdesk management',
      'A retail operation with high order enquiry and returns volumes',
      'Any business wanting to reduce response times without increasing headcount',
    ],
    seoTitle: 'Customer Experience & Support Services | NKP VENTURES',
    seoDesc: 'Professional customer experience services — email support, live chat, ticket management, helpdesk, and customer follow-up for growing businesses.',
    faqs: [
      { q: 'Will customers know support is outsourced?', a: 'Only if you choose to communicate this. We operate as a seamless extension of your team using your brand voice and tone.' },
      { q: 'What support platforms do you work with?', a: 'Zendesk, Freshdesk, Intercom, Gorgias, HubSpot, email, and most major support platforms.' },
      { q: 'Can you handle order and returns queries?', a: 'Yes. Order queries, returns management, and dispute resolution are all within the scope of our customer experience service.' },
    ],
  },
  {
    id: 'finance-accounting-support',
    slug: 'finance-accounting-support',
    title: 'Finance & Accounting Support',
    shortTitle: 'Finance & Accounting',
    tagline: 'Financial clarity that supports confident decision-making',
    icon: DollarSign,
    color: '#153E5C',
    description: 'Our Finance & Accounting Support pillar manages the essential financial administration functions — bookkeeping, invoice processing, reconciliation, and reporting — keeping your financial records accurate, your operations compliant, and your leadership informed.',
    services: [
      'Bookkeeping Support',
      'Invoice Processing',
      'Accounts Receivable',
      'Accounts Payable',
      'Expense Management',
      'Financial Data Entry',
      'Reconciliation Support',
    ],
    benefits: [
      'Accurate, up-to-date financial records at all times',
      'Reduced risk of compliance and reporting issues',
      'Cost savings versus dedicated in-house finance staff',
      'Timely financial reporting for better business decisions',
    ],
    useCases: [
      'A business with bookkeeping backlogs needing structured catch-up and ongoing maintenance',
      'An SME processing high invoice volumes requiring reliable AP/AR management',
      'A scaling business needing financial data entry and reconciliation support',
      'A company wanting financial operations handled without the overhead of a full finance team',
    ],
    seoTitle: 'Finance & Accounting Support Services | NKP VENTURES',
    seoDesc: 'Professional finance and accounting support — bookkeeping, invoice processing, accounts receivable, accounts payable, and reconciliation services.',
    faqs: [
      { q: 'Which accounting platforms do you support?', a: 'Xero, QuickBooks, FreshBooks, Sage, and most major accounting platforms.' },
      { q: 'Is financial data handled securely?', a: 'Yes. All financial data is handled under strict confidentiality agreements with controlled, role-based access protocols.' },
      { q: 'Do you provide regulated financial advice or file tax returns?', a: 'We provide financial records and reporting support. Regulated financial advice and tax filing remain with your accountant or tax advisor.' },
    ],
  },
  {
    id: 'hr-recruitment',
    slug: 'hr-recruitment',
    title: 'HR & Recruitment Support',
    shortTitle: 'HR & Recruitment',
    tagline: 'People operations handled professionally — so you can focus on your people',
    icon: Users,
    color: '#0E5A57',
    description: 'Our HR & Recruitment Support pillar manages the administrative and operational dimensions of human resources — from candidate screening and interview scheduling to onboarding documentation, HR administration, and payroll support.',
    services: [
      'Recruitment Assistance',
      'Candidate Screening',
      'Interview Scheduling',
      'Employee Documentation',
      'HR Administration',
      'Onboarding Support',
      'Payroll Administration Support',
    ],
    benefits: [
      'Professional, consistent HR administration across the employee lifecycle',
      'Faster, more structured recruitment processes',
      'Reduced management time spent on HR paperwork',
      'Improved documentation quality and compliance readiness',
    ],
    useCases: [
      'A growing business running active recruitment campaigns needing screening and coordination support',
      'An HR manager overwhelmed by administrative tasks requiring dedicated operational support',
      'A company standardising onboarding processes for a better employee experience',
      'A business needing payroll administration support without a full-time HR function',
    ],
    seoTitle: 'HR & Recruitment Support Services | NKP VENTURES',
    seoDesc: 'Professional HR and recruitment support — candidate screening, interview scheduling, onboarding, employee documentation, and HR administration.',
    faqs: [
      { q: 'Do you provide HR consulting or legal HR advice?', a: 'We provide HR administrative and operational support. Legal HR advice remains with your qualified HR advisor or employment solicitor.' },
      { q: 'Can you manage the full recruitment process?', a: 'We manage the administrative dimensions — postings, screening, correspondence, and scheduling. Final hiring decisions remain with your team.' },
      { q: 'How is employee data protected?', a: 'All employee data is handled under strict confidentiality agreements with role-based access controls and no third-party sharing.' },
    ],
  },
  {
    id: 'digital-solutions',
    slug: 'digital-solutions',
    title: 'Digital Solutions',
    shortTitle: 'Digital Solutions',
    tagline: 'From website to visibility — digital capabilities that drive growth',
    icon: Globe,
    color: '#2E7D7A',
    description: 'Our Digital Solutions pillar brings together website development, SEO, digital marketing, social media management, and content creation — giving growing businesses the complete digital capability they need to build their online presence, attract customers, and scale.',
    services: [
      'Website Development',
      'Website Maintenance',
      'WordPress Development',
      'E-commerce Website Development',
      'Landing Page Development',
      'UI/UX Support',
      'Website Content Management',
      'SEO',
      'Digital Marketing',
      'Social Media Management',
      'Content Creation',
      'Email Marketing',
      'Google Ads Management',
      'Meta Ads Management',
      'Lead Generation',
    ],
    benefits: [
      'A professional, high-performing online presence that represents your brand',
      'Improved organic visibility through structured, consistent SEO practices',
      'Consistent social media presence without the overhead of an in-house team',
      'Performance-led paid advertising that generates qualified, measurable leads',
    ],
    useCases: [
      'A business needing a professional website built or maintained without an in-house development team',
      'A brand wanting structured SEO and content to improve organic search visibility',
      'A company running paid advertising needing expert Google Ads and Meta campaign management',
      'A growing business wanting social media and content handled professionally and consistently',
    ],
    seoTitle: 'Digital Solutions — Website Development, SEO & Digital Marketing | NKP VENTURES',
    seoDesc: 'Digital solutions for growing businesses — website development, SEO, social media management, content creation, Google Ads, Meta Ads, and lead generation.',
    faqs: [
      { q: 'Do you build websites from scratch or only maintain existing ones?', a: 'Both. We build new websites (WordPress, Shopify, WooCommerce, custom) and maintain, update, and optimise existing websites.' },
      { q: 'How do you measure digital marketing performance?', a: 'We provide regular performance reports covering rankings, traffic, ad spend efficiency, and key conversion metrics relevant to your goals.' },
      { q: 'Can you manage both organic and paid digital marketing?', a: 'Yes. We manage both organic strategies (SEO, content, social) and paid channels (Google Ads, Meta Ads) — independently or as an integrated programme.' },
    ],
  },
  {
    id: 'ecommerce-marketplace-management',
    slug: 'ecommerce-marketplace-management',
    title: 'E-commerce & Marketplace Management',
    shortTitle: 'E-commerce & Marketplace',
    tagline: 'Expert operations for Amazon, eBay, Walmart, Etsy, Shopify and more',
    icon: ShoppingCart,
    color: '#153E5C',
    description: 'Our E-commerce & Marketplace Management pillar provides comprehensive operational support for online sellers — from product listings and catalogue management to order processing, inventory, customer support, and marketplace account administration across all major platforms.',
    services: [
      'Amazon Account Management',
      'eBay Account Management',
      'Walmart Marketplace Management',
      'Etsy Management',
      'Shopify Store Management',
      'WooCommerce Management',
      'Product Listing',
      'Product Catalogue Management',
      'Product Image & Content Management',
      'Order Management',
      'Inventory Management',
      'Pricing & Promotion Management',
      'Marketplace SEO',
      'Customer Support',
      'Returns & Refund Management',
      'Marketplace Account Health Monitoring',
      'Seller Central / Marketplace Administration',
    ],
    marketplaces: ['Amazon', 'eBay', 'Walmart', 'Etsy', 'Shopify', 'WooCommerce'],
    benefits: [
      'Professional marketplace operations without the cost of a full in-house team',
      'Accurate, optimised product listings that improve search visibility and conversion',
      'Structured order and inventory management that reduces errors and delays',
      'Consistent customer support across all marketplace channels',
    ],
    useCases: [
      'A brand selling on Amazon or eBay needing experienced support for listings, orders, and account health monitoring',
      'A Shopify or WooCommerce store owner wanting to scale without proportionally increasing headcount',
      'A multi-channel retailer needing coordinated management across multiple marketplace platforms',
      'A business entering new marketplaces (Walmart, Etsy) needing professional setup and ongoing operations',
    ],
    seoTitle: 'E-commerce & Marketplace Management Services | NKP VENTURES',
    seoDesc: 'E-commerce and marketplace management for Amazon, eBay, Walmart, Etsy, Shopify, WooCommerce. Product listings, orders, inventory, customer support, and account administration.',
    faqs: [
      { q: 'Are you an official partner of Amazon, eBay, Shopify or other platforms?', a: 'NKP VENTURES provides operational support for sellers on these platforms. We are not an officially authorised partner of Amazon, eBay, Walmart, Etsy, Shopify, or WooCommerce.' },
      { q: 'Can you manage accounts across multiple marketplaces simultaneously?', a: 'Yes. We manage multi-channel operations across different marketplaces with coordinated reporting and consistent management standards.' },
      { q: 'What marketplace account issues can you help resolve?', a: 'We support account health monitoring, policy compliance, listing issues, performance notifications, and general seller account administration.' },
    ],
  },
];

export const getPillarBySlug = (slug) => servicePillars.find(p => p.slug === slug);
export const PILLAR_SLUGS = new Set(servicePillars.map(p => p.slug));
